import { clientsApi } from "#/api/clientsApi";
import { uploadApi } from "#/api/uploadApi";
import {
  showErrorNotification,
  showSuccessNotification,
} from "#/components/notifications";
import DetailPageLayout from "#/components/ui/DetailPageLayout";
import {
  type CreateClientPayload,
  SupportedAuthenticationMethod,
} from "#/types/client";
import { ItemStatus } from "#/types/items";
import { sanitize, sanitizeItemStatusLabel } from "#/utils/StringUtils";
import {
  Accordion,
  Button,
  ColorInput,
  Menu,
  Select,
  Switch,
  Tabs,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronDown, IconImageInPicture, IconPlus } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { dashboardRoutes } from "#/routes/dashboardRoutes";
import { tableColumnsConfig, getTableColumnKeys } from "#/config/tableColumnsConfig";
import { dateTimeFormatPresets } from "#/config/dateTimeFormats";
import ImportUsersDrawer from "./components/ImportUsersDrawer";
import UsersTable from "./components/UsersTable";
import { useTranslation } from "react-i18next";

const ClientDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [logoFile, setLogoFile] = useState<File>();
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [activeTab, setActiveTab] = useState<string>(searchParams.get("tab") || "details");

  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    if (tabFromUrl && (tabFromUrl === "details" || tabFromUrl === "team")) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  const [
    addUsersDrawerOpened,
    { open: openAddUsersDrawer, close: closeAddUsersDrawer },
  ] = useDisclosure(false);
  const [importMode, setImportMode] = useState<"manual" | "csv">("csv");
  const [refetchUsers, setRefetchUsers] = useState(0);

  const form = useForm<CreateClientPayload>({
    initialValues: {
      name: "",
      slug: "",
      color: "",
      supportedAuthenticationMethods: SupportedAuthenticationMethod.PASSWORD,
      address: "",
      contactEmail: "",
      allowedRoutes: undefined,
      visibleColumns: undefined,
      dateFormat: undefined,
      timeFormat: undefined,
      allowedStatuses: undefined,
    },
    validate: {
      name: (value) => (value ? null : t("clients.validation.nameRequired")),
      slug: (value) => (value ? null : t("clients.validation.slugRequired")),
      color: (value) => (value ? null : t("clients.validation.colorRequired")),
      address: (value) => (value ? null : t("clients.validation.addressRequired")),
      contactEmail: (value) => {
        if (!value) {
          return t("clients.validation.contactEmailRequired");
        }
        return /^\S+@\S+$/.test(value) ? null : t("clients.validation.invalidEmail");
      },
    },
  });

  const { refetch } = useQuery({
    queryKey: ["client", id],
    queryFn: async () => {
      if (!id || id === "new") {
        return null;
      }

      try {
        const response = await clientsApi.getById(Number(id));
        form.setValues({
          name: response.data.name,
          color: response.data.color,
          slug: response.data.slug,
          supportedAuthenticationMethods:
            response.data.supportedAuthenticationMethods,
          address: response.data.address,
          contactEmail: response.data.contactEmail,
          allowedRoutes: response.data.allowedRoutes,
          visibleColumns: response.data.visibleColumns,
          dateFormat: response.data.dateFormat,
          timeFormat: response.data.timeFormat,
          allowedStatuses: response.data.allowedStatuses,
        });
        setPreviewUrl(response.data.logo);
        form.resetDirty();
        return { client: response.data };
      } catch (error) {
        console.log("🚀 ~ queryFn: ~ error:", error);
        throw new Error("No client returned");
      }
    },
    enabled: !!id && id !== "new",
    retry: false,
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const fileUrl = URL.createObjectURL(selectedFile);
      setPreviewUrl(fileUrl);
      setLogoFile(selectedFile);
    }
  };

  const handleSubmit = (values: typeof form.values) => {
    clientMutation.mutate(values);
  };

  const clientMutation = useMutation({
    mutationFn: async (values: CreateClientPayload) => {
      const payload = { ...values };
      if (logoFile) {
        const logoUrl = await uploadApi.upload(logoFile);
        payload.logo = logoUrl;
      }

      if (id !== "new") {
        return await clientsApi.update(Number(id), payload);
      } else {
        return await clientsApi.create(payload);
      }
    },
    onSuccess: (data) => {
      if (id === "new") {
        void navigate(`/clients/${data.data.id}`);
      }
      showSuccessNotification({
        title: t("common.success"),
        message: id === "new" ? t("clients.createdSuccess") : t("clients.createdSuccess"),
        color: "green.5",
      });
      void refetch();
    },
    onError: (error) => {
      console.error("🚀 ~ handleSubmit ~ error:", error);
      showErrorNotification({
        message: error.message ?? t("common.failedToProcess"),
      });
    },
  });

  const updateAllowedRoutesMutation = useMutation({
    mutationFn: async (allowedRoutes: string[] | undefined) => {
      if (id === "new") return;
      return await clientsApi.update(Number(id), { allowedRoutes });
    },
    onSuccess: () => {
      form.setFieldValue("allowedRoutes", form.values.allowedRoutes);
      form.resetDirty();
      void refetch();
    },
    onError: (error) => {
      console.error("🚀 ~ updateAllowedRoutes ~ error:", error);
      showErrorNotification({
        message: error.message ?? t("common.failedToProcess"),
      });
    },
  });

  const getAllRoutePaths = (): string[] => {
    const paths: string[] = [];
    dashboardRoutes.forEach((route) => {
      paths.push(route.href);
      if (route.children) {
        route.children.forEach((child) => {
          paths.push(route.href + child.href);
        });
      }
    });
    return paths;
  };

  const isRouteAllowed = (routePath: string): boolean => {
    // If allowedRoutes is undefined or empty, all routes are allowed
    if (!form.values.allowedRoutes || form.values.allowedRoutes.length === 0) {
      return true;
    }
    return form.values.allowedRoutes.includes(routePath);
  };

  const handleRouteToggle = (routePath: string, checked: boolean) => {
    const isCurrentlyAllAllowed = !form.values.allowedRoutes || form.values.allowedRoutes.length === 0;

    let newAllowedRoutes: string[];

    if (isCurrentlyAllAllowed && !checked) {
      // If all routes are currently allowed and we're toggling OFF a route,
      // start with ALL routes, then remove the one being toggled
      newAllowedRoutes = getAllRoutePaths();
    } else {
      // Otherwise use existing allowedRoutes or empty array
      newAllowedRoutes = form.values.allowedRoutes
        ? [...form.values.allowedRoutes]
        : [];
    }

    if (checked) {
      // Add route if not already present
      if (!newAllowedRoutes.includes(routePath)) {
        newAllowedRoutes.push(routePath);
      }
    } else {
      // Remove route and all its children
      newAllowedRoutes = newAllowedRoutes.filter((route) => {
        // Remove the route itself
        if (route === routePath) return false;
        // Remove children of this route
        if (route.startsWith(routePath + "/")) return false;
        return true;
      });
    }

    form.setFieldValue("allowedRoutes", newAllowedRoutes);
    updateAllowedRoutesMutation.mutate(newAllowedRoutes);
  };

  // Column Visibility Logic
  const updateVisibleColumnsMutation = useMutation({
    mutationFn: async (visibleColumns: Record<string, string[]> | undefined) => {
      if (id === "new") return;
      return await clientsApi.update(Number(id), { visibleColumns });
    },
    onSuccess: () => {
      form.setFieldValue("visibleColumns", form.values.visibleColumns);
      form.resetDirty();
      void refetch();
    },
    onError: (error) => {
      console.error("🚀 ~ updateVisibleColumns ~ error:", error);
      showErrorNotification({
        message: error.message ?? t("common.failedToProcess"),
      });
    },
  });

  const isColumnVisible = (tableName: string, columnKey: string): boolean => {
    if (!form.values.visibleColumns) {
      return true;
    }
    const tableColumns = form.values.visibleColumns[tableName];
    if (!tableColumns || tableColumns.length === 0) {
      return true;
    }
    return tableColumns.includes(columnKey);
  };

  const handleColumnToggle = (tableName: string, columnKey: string, checked: boolean) => {
    const currentColumns = form.values.visibleColumns || {};
    const tableColumns = currentColumns[tableName] || [];

    // If table has no config yet (all columns visible)
    const isCurrentlyAllVisible = tableColumns.length === 0;

    let newTableColumns: string[];

    if (isCurrentlyAllVisible && !checked) {
      // Start with all columns, then remove the unchecked one
      newTableColumns = getTableColumnKeys(tableName).filter((key) => key !== columnKey);
    } else {
      newTableColumns = [...tableColumns];
      if (checked) {
        if (!newTableColumns.includes(columnKey)) {
          newTableColumns.push(columnKey);
        }
      } else {
        newTableColumns = newTableColumns.filter((key) => key !== columnKey);
      }
    }

    const newVisibleColumns = {
      ...currentColumns,
      [tableName]: newTableColumns,
    };

    form.setFieldValue("visibleColumns", newVisibleColumns);
    updateVisibleColumnsMutation.mutate(newVisibleColumns);
  };

  const handleShowAll = (tableName: string) => {
    const currentColumns = form.values.visibleColumns || {};
    const newVisibleColumns = {
      ...currentColumns,
      [tableName]: getTableColumnKeys(tableName),
    };
    form.setFieldValue("visibleColumns", newVisibleColumns);
    updateVisibleColumnsMutation.mutate(newVisibleColumns);
  };

  const handleHideAll = (tableName: string) => {
    const currentColumns = form.values.visibleColumns || {};
    const newVisibleColumns = {
      ...currentColumns,
      [tableName]: [],
    };
    form.setFieldValue("visibleColumns", newVisibleColumns);
    updateVisibleColumnsMutation.mutate(newVisibleColumns);
  };

  // Date & Time Format Logic
  const updateDateTimeFormatMutation = useMutation({
    mutationFn: async (payload: { dateFormat?: string; timeFormat?: string }) => {
      if (id === "new") return;
      return await clientsApi.update(Number(id), payload);
    },
    onSuccess: () => {
      form.setFieldValue("dateFormat", form.values.dateFormat);
      form.setFieldValue("timeFormat", form.values.timeFormat);
      form.resetDirty();
      void refetch();
    },
    onError: (error) => {
      console.error("🚀 ~ updateDateTimeFormat ~ error:", error);
      showErrorNotification({
        message: error.message ?? t("common.failedToProcess"),
      });
    },
  });

  const handleFormatSelect = (preset: typeof dateTimeFormatPresets[0]) => {
    form.setFieldValue("dateFormat", preset.dateFormat);
    form.setFieldValue("timeFormat", preset.timeFormat);
    updateDateTimeFormatMutation.mutate({
      dateFormat: preset.dateFormat,
      timeFormat: preset.timeFormat,
    });
  };

  const getCurrentPresetId = (): string | undefined => {
    if (!form.values.dateFormat || !form.values.timeFormat) {
      return undefined;
    }
    const matchingPreset = dateTimeFormatPresets.find(
      (preset) =>
        preset.dateFormat === form.values.dateFormat &&
        preset.timeFormat === form.values.timeFormat
    );
    return matchingPreset?.id;
  };

  // Allowed Statuses Logic
  const updateAllowedStatusesMutation = useMutation({
    mutationFn: async (allowedStatuses: ItemStatus[] | undefined) => {
      if (id === "new") return;
      return await clientsApi.update(Number(id), { allowedStatuses });
    },
    onSuccess: () => {
      form.setFieldValue("allowedStatuses", form.values.allowedStatuses);
      form.resetDirty();
      void refetch();
    },
    onError: (error) => {
      console.error("🚀 ~ updateAllowedStatuses ~ error:", error);
      showErrorNotification({
        message: error.message ?? t("common.failedToProcess"),
      });
    },
  });

  const isStatusAllowed = (status: ItemStatus): boolean => {
    // If allowedStatuses is undefined or empty, all statuses are allowed
    if (!form.values.allowedStatuses || form.values.allowedStatuses.length === 0) {
      return true;
    }
    return form.values.allowedStatuses.includes(status);
  };

  const handleStatusToggle = (status: ItemStatus, checked: boolean) => {
    const isCurrentlyAllAllowed = !form.values.allowedStatuses || form.values.allowedStatuses.length === 0;

    let newAllowedStatuses: ItemStatus[];

    if (isCurrentlyAllAllowed && !checked) {
      // If all statuses are currently allowed and we're toggling OFF a status,
      // start with ALL statuses, then remove the one being toggled
      newAllowedStatuses = Object.values(ItemStatus).filter((s) => s !== status);
    } else {
      // Otherwise use existing allowedStatuses or empty array
      newAllowedStatuses = form.values.allowedStatuses
        ? [...form.values.allowedStatuses]
        : [];
    }

    if (checked) {
      // Add status if not already present
      if (!newAllowedStatuses.includes(status)) {
        newAllowedStatuses.push(status);
      }
    } else {
      // Remove status
      newAllowedStatuses = newAllowedStatuses.filter((s) => s !== status);
    }

    form.setFieldValue("allowedStatuses", newAllowedStatuses);
    updateAllowedStatusesMutation.mutate(newAllowedStatuses);
  };

  return (
    <DetailPageLayout
      title={id !== "new" ? t("clients.clientDetails") : t("clients.createClient")}
      navRightSection={
        activeTab === "team" && id !== "new" ? (
          <Menu>
            <Menu.Target>
              <Button
                leftSection={<IconPlus size={18} />}
                rightSection={<IconChevronDown size={16} />}
                color="black"
                size="xs"
              >
                Add Users
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                onClick={() => {
                  setImportMode("manual");
                  openAddUsersDrawer();
                }}
              >
                Add Manually
              </Menu.Item>
              <Menu.Item
                onClick={() => {
                  setImportMode("csv");
                  openAddUsersDrawer();
                }}
              >
                Import from CSV
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        ) : null
      }
    >
      <div className="h-full flex flex-col min-h-0">
        <Tabs defaultValue="details" className="w-full h-full flex flex-col" value={activeTab} onChange={(value) => {
          const newTab = value || "details";
          setActiveTab(newTab);
          setSearchParams({ tab: newTab });
        }}>
          <Tabs.List className="flex-shrink-0">
            <Tabs.Tab value="details">{ t("clients.tabs.details") }</Tabs.Tab>
            {id !== "new" && <Tabs.Tab value="team">{ t("clients.tabs.teamMembers") }</Tabs.Tab>}
          </Tabs.List>

          <Tabs.Panel value="details" pt="md" className="flex-1 overflow-auto min-h-0">
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <div className="w-full grid grid-cols-1 gap-4">
                <Accordion variant={"separated"} defaultValue={["basic"]} multiple>
                  <Accordion.Item value={"basic"}>
                    <Accordion.Control>
                      <div className="flex items-center justify-between w-full pr-4">
                        <span>{ t("common.basicDetails") }</span>
                        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button
                            color="black"
                            variant="outline"
                            type="reset"
                            disabled={!form.isDirty()}
                            onClick={() => {
                              form.reset();
                            }}
                            size="xs"
                          >
                            Clear
                          </Button>

                          <Button
                            color="black"
                            variant="filled"
                            loading={clientMutation.isPending}
                            disabled={!form.isDirty()}
                            onClick={() => {
                              form.onSubmit(handleSubmit)();
                            }}
                            size="xs"
                          >
                            {id === "new" ? t("common.create") : t("common.save")}
                          </Button>
                        </div>
                      </div>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <div className="w-full flex flex-col gap-4">
                        <div className="w-full grid grid-cols-2 gap-4">
                          <TextInput
                            label={t("clients.form.name")}
                            withAsterisk
                            placeholder={t("clients.form.namePlaceholder")}
                            {...form.getInputProps("name")}
                          />

                          <TextInput
                            label={t("clients.form.slug")}
                            withAsterisk
                            placeholder={t("clients.form.slugPlaceholder")}
                            {...form.getInputProps("slug")}
                          />

                          <Select
                            label={t("clients.form.authMethod")}
                            placeholder={t("clients.form.authMethodPlaceholder")}
                            searchable
                            data={Object.values(SupportedAuthenticationMethod).map(
                              (status) => ({
                                label: sanitize(status),
                                value: status,
                              })
                            )}
                            value={form.values.supportedAuthenticationMethods}
                            clearable
                            onChange={(_value) => {
                              form.setValues({
                                ...form.values,
                                supportedAuthenticationMethods:
                                  _value as SupportedAuthenticationMethod,
                              });
                            }}
                          />

                          <TextInput
                            label={t("clients.form.address")}
                            withAsterisk
                            placeholder={t("clients.form.addressPlaceholder")}
                            {...form.getInputProps("address")}
                          />

                          <TextInput
                            label={t("clients.form.contactEmail")}
                            withAsterisk
                            placeholder={t("clients.form.contactEmailPlaceholder")}
                            {...form.getInputProps("contactEmail")}
                          />

                          <ColorInput
                            label={t("clients.form.color")}
                            withAsterisk
                            placeholder={t("clients.form.colorPlaceholder")}
                            {...form.getInputProps("color")}
                          />
                        </div>

                        <div className="w-full flex flex-col gap-2 relative">
                          <span className="text-xs font-bold text-slate-600">
                            Logo
                          </span>
                          <div className="w-full grid grid-cols-[auto_1fr] items-center gap-4 text-gray-400">
                            <div className="w-24 h-24 border border-gray-200 rounded-lg flex items-center justify-center relative">
                              {!previewUrl ? (
                                <IconImageInPicture size={40} />
                              ) : (
                                <img
                                  src={previewUrl}
                                  alt="Logo"
                                  className="h-full w-full object-cover"
                                />
                              )}
                            </div>
                            <div className="flex flex-col gap-4 w-full">
                              <span>{ t("clients.form.selectImage") }</span>
                              <Button variant="outline">{ t("clients.form.selectFile") }</Button>
                            </div>
                          </div>
                          <input
                            type="file"
                            accept="image/jpeg, image/png"
                            className="w-full h-full absolute top-0 left-0 z-10 opacity-0 cursor-pointer"
                            onChange={handleFileSelect}
                          />
                        </div>
                      </div>
                    </Accordion.Panel>
                  </Accordion.Item>

                  {id !== "new" && (
                    <Accordion.Item value={"allowed_routes"}>
                      <Accordion.Control>{ t("clients.sections.allowedRoutes") }</Accordion.Control>
                      <Accordion.Panel>
                        <div className="w-full flex flex-col gap-3">
                          <div className="text-sm text-gray-600 mb-2">
                            Control which navigation routes this client can access. If no routes are selected, all routes will be available.
                          </div>
                          {dashboardRoutes.map((route) => {
                            const parentPath = route.href;
                            const isParentAllowed = isRouteAllowed(parentPath);

                            return (
                              <div key={route.href} className="flex flex-col gap-2">
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                  <div className="flex items-center gap-2">
                                    <route.icon size={18} />
                                    <span className="font-medium">{t(route.label)}</span>
                                  </div>
                                  <Switch
                                    checked={isParentAllowed}
                                    onChange={(e) =>
                                      handleRouteToggle(parentPath, e.currentTarget.checked)
                                    }
                                    disabled={updateAllowedRoutesMutation.isPending}
                                  />
                                </div>

                                {route.children && route.children.length > 0 && (
                                  <div className="ml-8 flex flex-col gap-2">
                                    {route.children.map((child) => {
                                      const childPath = parentPath + child.href;
                                      const isChildAllowed = isRouteAllowed(childPath);

                                      return (
                                        <div
                                          key={child.href}
                                          className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                                        >
                                          <div className="flex items-center gap-2">
                                            <child.icon size={16} />
                                            <span className="text-sm">{t(child.label)}</span>
                                          </div>
                                          <Switch
                                            checked={isChildAllowed}
                                            onChange={(e) =>
                                              handleRouteToggle(
                                                childPath,
                                                e.currentTarget.checked
                                              )
                                            }
                                            disabled={
                                              !isParentAllowed ||
                                              updateAllowedRoutesMutation.isPending
                                            }
                                          />
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </Accordion.Panel>
                    </Accordion.Item>
                  )}

                  {id !== "new" && (
                    <Accordion.Item value={"column_visibility"}>
                      <Accordion.Control>{ t("clients.sections.columnVisibility") }</Accordion.Control>
                      <Accordion.Panel>
                        <div className="w-full flex flex-col gap-3">
                          <div className="text-sm text-gray-600 mb-2">
                            Control which columns are visible in table pages for this client. If no columns are selected for a table, all columns will be visible.
                          </div>
                          <Tabs defaultValue={Object.keys(tableColumnsConfig)[0]}>
                            <Tabs.List>
                              {Object.keys(tableColumnsConfig).map((tableName) => (
                                <Tabs.Tab key={tableName} value={tableName}>
                                  {t(tableColumnsConfig[tableName].tableName)}
                                </Tabs.Tab>
                              ))}
                            </Tabs.List>

                            {Object.keys(tableColumnsConfig).map((tableName) => {
                              const config = tableColumnsConfig[tableName];

                              return (
                                <Tabs.Panel key={tableName} value={tableName} pt="md">
                                  <div className="w-full flex flex-col gap-3">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Button
                                        size="xs"
                                        variant="outline"
                                        color="black"
                                        onClick={() => handleShowAll(tableName)}
                                        disabled={updateVisibleColumnsMutation.isPending}
                                      >
                                        {t("common.showAll")}
                                      </Button>
                                      <Button
                                        size="xs"
                                        variant="outline"
                                        color="gray"
                                        onClick={() => handleHideAll(tableName)}
                                        disabled={updateVisibleColumnsMutation.isPending}
                                      >
                                        {t("common.hideAll")}
                                      </Button>
                                    </div>

                                    {config.columns.map((column) => (
                                      <div
                                        key={column.key}
                                        className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                                      >
                                        <span className="text-sm font-medium">
                                          {t(column.label)}
                                        </span>
                                        <Switch
                                          checked={isColumnVisible(tableName, column.key)}
                                          onChange={(e) =>
                                            handleColumnToggle(
                                              tableName,
                                              column.key,
                                              e.currentTarget.checked
                                            )
                                          }
                                          disabled={updateVisibleColumnsMutation.isPending}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </Tabs.Panel>
                              );
                            })}
                          </Tabs>
                        </div>
                      </Accordion.Panel>
                    </Accordion.Item>
                  )}

                  {id !== "new" && (
                    <Accordion.Item value={"date_time_format"}>
                      <Accordion.Control>{ t("clients.sections.dateTimeFormat") }</Accordion.Control>
                      <Accordion.Panel>
                        <div className="w-full flex flex-col gap-3">
                          <div className="text-sm text-gray-600 mb-2">
                            Select how dates and times should be displayed throughout the application for this client. If no format is selected, the default format (DD/MM/YYYY HH:mm) will be used.
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            {dateTimeFormatPresets.map((preset) => {
                              const isSelected = getCurrentPresetId() === preset.id;
                              const isDefault = !form.values.dateFormat && !form.values.timeFormat && preset.id === "eu_24";

                              return (
                                <Button
                                  key={preset.id}
                                  variant={isSelected || isDefault ? "filled" : "outline"}
                                  color={isSelected || isDefault ? "black" : "gray"}
                                  className="h-auto p-3"
                                  onClick={() => handleFormatSelect(preset)}
                                  disabled={updateDateTimeFormatMutation.isPending}
                                >
                                  <div className="flex flex-col items-start w-full gap-1">
                                    <span className="text-sm font-semibold">{t(preset.label)}</span>
                                    <span className="text-xs opacity-80">{preset.example}</span>
                                  </div>
                                </Button>
                              );
                            })}
                          </div>
                        </div>
                      </Accordion.Panel>
                    </Accordion.Item>
                  )}

                  {id !== "new" && (
                    <Accordion.Item value={"allowed_statuses"}>
                      <Accordion.Control>{ t("clients.sections.allowedStatuses") }</Accordion.Control>
                      <Accordion.Panel>
                        <div className="w-full flex flex-col gap-3">
                          <div className="text-sm text-gray-600 mb-2">
                            Control which item statuses are available for this client. If no statuses are selected, all statuses will be available.
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            {Object.values(ItemStatus).map((status) => {
                              const isAllowed = isStatusAllowed(status);

                              return (
                                <div
                                  key={status}
                                  className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg shadow-sm"
                                >
                                  <span className="font-medium text-sm">{sanitizeItemStatusLabel(status)}</span>
                                  <Switch
                                    checked={isAllowed}
                                    onChange={(e) =>
                                      handleStatusToggle(status, e.currentTarget.checked)
                                    }
                                    disabled={updateAllowedStatusesMutation.isPending}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </Accordion.Panel>
                    </Accordion.Item>
                  )}
                </Accordion>
              </div>
            </form>
          </Tabs.Panel>

          {id !== "new" && (
            <Tabs.Panel value="team" pt="md" className="flex-1 flex flex-col min-h-0">
              <UsersTable clientId={Number(id)} key={refetchUsers} />
            </Tabs.Panel>
          )}
        </Tabs>
      </div>

      <ImportUsersDrawer
        opened={addUsersDrawerOpened}
        onClose={closeAddUsersDrawer}
        clientId={Number(id)}
        mode={importMode}
        onSuccess={() => {
          setRefetchUsers(prev => prev + 1);
          closeAddUsersDrawer();
        }}
      />
    </DetailPageLayout>
  );
};

export default ClientDetailPage;
